import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInitialData } from '../components/data/DataContext';
import SeoHead from '../seo/seoHead';
import { useLocation, useSearchParams, Link } from 'react-router-dom';
import Breadcrumbs from '../seo/breadcrumbs';
import Pagination from '../components/ui/pagination';
import languages from "../locales/languages";
import CTAHover from '../components/ui/CTAHover';
import { Badge } from "../components/ui/badge";
import { LayoutList } from 'lucide-react';
import { API_URL } from '../config';

const Lists = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { lists = [], total = 0, seo = {} } = useInitialData();
  const page  = Number(searchParams.get('p') || 1);
  const limit = 8;
  const [listsState, setLists] = useState(lists);
  const [totalState, setTotal] = useState(total);
  const {
    title, description, canonicalUrl,
    ogTitle, ogDescription, ogImage, ogUrl,
    ogType, twitterCard, prevUrl, nextUrl,
    alternates,
  } = seo;

  const segments     = location.pathname.split('/').filter(Boolean);
  const langFromUrl  = segments[0];
  const currentLang  = languages.some(l => l.code === langFromUrl) ? langFromUrl : 'en';
  const prefix       = currentLang === 'en' ? '' : `/${currentLang}`;

  const firstRender = useRef(true);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      if (page === 1 && listsState.length) return;
    }

    const fetchLists = async () => {
      try {
        const res  = await fetch(`${API_URL}/lists?limit=${limit}&page=${page}`);
        const data = await res.json();
        setLists(data.lists || []);
        setTotal(data.total || 0);
      } catch (err) {
        console.error('Error fetching lists:', err);
      }
    };

    fetchLists();
  }, [page]);

  const changePage = newPage => {
    if (newPage === page) return;
    const params = {};
    if (newPage !== 1) params.p = newPage;
    setSearchParams(params, { replace: true });
  };

  const firstPart  = listsState.slice(0, 4);
  const secondPart = listsState.slice(4);

  const totalPages = Math.ceil(totalState / limit);

  const fmtDate = d => new Intl.DateTimeFormat("en-US", {
    month: "short", day: "numeric", year: "numeric", timeZone: "UTC",
  }).format(new Date(d));

  return (
    <>
      <SeoHead
        lang={currentLang}
        title={title}
        description={description}
        canonicalUrl={canonicalUrl}
        ogTitle={ogTitle}
        ogDescription={ogDescription}
        ogImage={ogImage}
        ogUrl={ogUrl}
        ogType={ogType}
        twitterCard={twitterCard}
        prevUrl={prevUrl}
        nextUrl={nextUrl}
        alternates={alternates}
        jsonLd={seo.jsonLd}
      />

      <div className="mx-auto max-w-7xl px-8 lg:px-0 my-8">
        <Breadcrumbs mapSegments={{ lists: t('menu.lists') }} pageTitle={t('menu.lists')} />

        <div className="flex items-center justify-start mb-8">
          <LayoutList className="w-6 h-6 mr-2" />
          <div className="text-4xl font-bold">{t('menu.lists')}</div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {firstPart.map(list => <ListCard key={list._id} list={list} prefix={prefix} fmtDate={fmtDate} />)}

          <PromoCard t={t} />

          {secondPart.map(list => <ListCard key={list._id} list={list} prefix={prefix} fmtDate={fmtDate} />)}
        </div>

        {totalPages > 1 && (
          <div className="mt-8 flex justify-center">
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={changePage} />
          </div>
        )}
      </div>
    </>
  );
};

const ListCard = ({ list, prefix, fmtDate }) => (
  <div className="rounded-xl overflow-hidden flex flex-col border border-gray-200 hover:shadow-lg transition-shadow h-full">
    <Link to={`${prefix}/lists/${list.url_slug}/`} className="flex flex-col h-full">
      {list.img_url && (
        <div className="w-full h-40 overflow-hidden">
          <img src={list.img_url} alt={list.title} className="w-full h-full object-cover" />
        </div>
      )}
      <div className="flex-grow p-4 flex flex-col justify-between">
        <div className="flex-grow">
          <h2 className="text-lg font-semibold leading-snug mb-2">{list.title}</h2>
          <p className="text-sm text-stone-700 leading-relaxed mb-2">{list.short_description}</p>
        </div>
        <Badge text={fmtDate(list.publish_date)} variant="v6" />
      </div>
    </Link>
  </div>
);

const PromoCard = ({ t }) => (
  <div className="rounded-xl overflow-hidden relative h-full flex flex-col justify-end">
    <div className="border border-black p-4 text-stone-900 h-full flex flex-col justify-center">
      <h3 className="text-center text-lg font-semibold mb-2">{t('other.t1')}</h3>
      <p className="text-center text-sm mb-4">{t('other.t2')}</p>
      <CTAHover
        className="mt-6"
        text={t('other.t3')}
        href="find-investors"
        align="center"
        bgColor="bg-blue-600 text-white "
        textColor="text-stone-700"
        hoverTextColor="text-white"
      />
    </div>
  </div>
);

export default Lists;
